import { UseMutationDefaultOptions } from '@front/hook/Api/common';
import { GZIP_CONTENT_ENCODING, GZIP_CONTENT_TYPE, GZIP_MIN_BYTES, gzipFile, isGzipSupported } from '@front/hook/Api/File/gzip';
import { DEFAULT_TIMEOUT } from '@front/hook/Api/utils/consts';
import { signalToTimeout } from '@front/hook/Api/utils/signalToTimeout';
import { FileRepository } from '@front/repository/Api/File';
import { useMutation } from '@tanstack/react-query';
import { AuthDomain } from 'auth-lib-front';

import Project = AuthDomain.Object.Project;

const repository = new FileRepository();

export type _UploadCsvVariables = {
  projectId: Project.Id;
  file: File;
  onProgress?: (ratio: number) => void;
};

export type _UploadCsvResult = {
  fileId: string;
};

/**
 * Signed URL でアップロード URL を取得 → GCS へ PUT までを行うアップロード専用フック。
 * CSV 解析（`/files/analyze`）は {@link _useAnalyzeCsv} で別途実行する。
 */
export const _useUploadCsv = () => {
  return useMutation({
    mutationFn: async (params: _UploadCsvVariables): Promise<_UploadCsvResult> => {
      const originalContentType = params.file.type || 'text/csv';

      // 一定サイズ以上は gzip 圧縮してから PUT する。転送量を ~1/3 に減らし、
      // アップロードとサーバー側の初期ロード(loadCsv)の両方を短縮する。
      // GCS には Content-Encoding を付けず application/gzip の生バイトとして保存し、
      // 読み取り側 (analyze / import job) で明示的に gunzip する。
      const shouldGzip = isGzipSupported() && params.file.size >= GZIP_MIN_BYTES;
      const body: Blob = shouldGzip ? await gzipFile(params.file) : params.file;
      const contentType = shouldGzip ? GZIP_CONTENT_TYPE : originalContentType;
      const contentEncoding = shouldGzip ? GZIP_CONTENT_ENCODING : undefined;

      const {
        uploadUrl,
        fileId,
        contentType: signedContentType,
      } = await repository.getUploadUrl(
        {
          projectId: params.projectId,
          fileName: params.file.name,
          // 行数推定などサーバー側計算は非圧縮サイズを前提とするため、元ファイルサイズを記録する。
          fileSize: params.file.size,
          contentType,
          contentEncoding,
        },
        { signal: signalToTimeout(DEFAULT_TIMEOUT.MUTATION) },
      );

      const putContentType = signedContentType || contentType;

      await repository.uploadToGcs({
        uploadUrl,
        file: body,
        contentType: putContentType,
        signal: signalToTimeout(DEFAULT_TIMEOUT.CSV_UPLOAD),
        onProgress: params.onProgress,
      });

      return { fileId };
    },
    ...UseMutationDefaultOptions,
  });
};
